import { useEffect, useState } from "react";
import { Box, Typography, Rating, Button, Chip } from "@mui/material";
import { PublicAdvisor } from "protogen/common_pb";
import { Review as ReviewProto, ReviewStatus } from "protogen/review_pb";
import { useListReviews, useUpdateReview } from "services/review";
import { Check, EyeOff } from "lucide-react";

type Props = {
  advisor: PublicAdvisor | undefined | null;
  isAdmin?: boolean;
};

export default ({ advisor, isAdmin = false }: Props) => {
  const [reviews, setReviews] = useState<ReviewProto[]>([]);
  const [loading, setLoading] = useState(true);
  const { request: listReviews } = useListReviews((response) => {
    if (response?.reviews) {
      setReviews(response.reviews);
    }
    setLoading(false);
  });

  const { request: updateReview } = useUpdateReview(() => {
    if (advisor?.ref) {
      setLoading(true);
      listReviews({
        subjectRef: advisor.ref,
        status: isAdmin ? undefined : ReviewStatus.APPROVED,
      });
    }
  });

  useEffect(() => {
    if (advisor?.ref) {
      setLoading(true);
      listReviews({
        subjectRef: advisor.ref,
        status: isAdmin ? undefined : ReviewStatus.APPROVED,
      });
    }
  }, [advisor?.ref, isAdmin]);

  if (loading || !reviews || reviews.length === 0) {
    return null;
  }

  const handleApprove = (reviewId: string) => {
    updateReview({
      ref: reviewId,
      status: ReviewStatus.APPROVED,
    });
  };

  const handleHide = (reviewId: string) => {
    updateReview({
      ref: reviewId,
      status: ReviewStatus.HIDDEN,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Typography variant="h3">Reviews</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {reviews.map((review) => (
          <Box
            key={review.ref}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: "#F9F9F9",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Typography variant="body" sx={{ fontWeight: 600 }}>
                      {review.displayName}
                    </Typography>
                    {isAdmin && (
                      <Chip
                        label={
                          review.status === ReviewStatus.PENDING
                            ? "Pending"
                            : review.status === ReviewStatus.APPROVED
                              ? "Approved"
                              : "Hidden"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            review.status === ReviewStatus.PENDING
                              ? "#FFA500"
                              : review.status === ReviewStatus.APPROVED
                                ? "#4CAF50"
                                : "#9E9E9E",
                          color: "white",
                          fontSize: "12px",
                          height: "20px",
                        }}
                      />
                    )}
                  </Box>
                  {review.createdSec && (
                    <Typography
                      variant="bodySmall"
                      sx={{ fontSize: "14px", color: "#666" }}
                    >
                      {new Date(
                        Number(review.createdSec) * 1000,
                      ).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
                <Rating value={review.starCount} readOnly size="small" />
                <Typography
                  variant="body"
                  sx={{
                    fontSize: "15px",
                    lineHeight: "141%",
                    color: "#3D3D3D",
                    marginTop: "8px",
                  }}
                >
                  {review.text}
                </Typography>
              </Box>
              {isAdmin && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  {review.status !== ReviewStatus.APPROVED && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<Check size={16} />}
                      onClick={() => handleApprove(review.ref)}
                    >
                      Approve
                    </Button>
                  )}
                  {review.status !== ReviewStatus.HIDDEN && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<EyeOff size={16} />}
                      onClick={() => handleHide(review.ref)}
                    >
                      Hide
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
